import React, { useState, useEffect, useRef } from 'react'

interface DropdownOption {
  value: string
  label: string
}

interface DropdownProps {
  options: DropdownOption[]
  onSelect: (value: string) => void
  value?: string
  defaultLabel?: string
  defaultValue?: string
  loading?: boolean
  disabled?: boolean
  emptyMessage?: string
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  onSelect,
  value,
  defaultLabel,
  defaultValue,
  loading = false,
  disabled = false,
  emptyMessage
}) => {
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (defaultValue) {
      const defaultOption = options.find((option) => option.value === defaultValue)
      if (defaultOption) {
        setSelectedOption(defaultOption)
      } else {
        setSelectedOption(null)
      }
    } else if (defaultLabel) {
      const defaultOption = options.find((option) => option.label === defaultLabel)
      if (defaultOption) {
        setSelectedOption(defaultOption)
      } else {
        setSelectedOption(null)
      }
    }
  }, [options, defaultLabel, defaultValue])

  
  const handleSelectOption = (option: DropdownOption) => {
    setSelectedOption(option)
    onSelect(option.value)
    setIsOpen(false)
  }

  const toggleDropdown = () => {
    if (!loading && !disabled) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <div className="relative text-left shadow-sm" ref={dropdownRef}>
      <div>
        <span className="rounded-md shadow-sm">
          <button
            type="button"
            className="flex justify-between w-full border border-[#CED4DA] bg-white px-3 sm:px-4 rounded-md py-2 text-xs sm:text-sm md:text-base font-medium text-[#6C757D] focus:outline-none items-center transition-colors disabled:cursor-not-allowed disabled:opacity-100 disabled:transform-none disabled:shadow-none enabled:hover:bg-gray-50"
            onClick={toggleDropdown}
            aria-haspopup="true"
            aria-expanded={isOpen ? 'true' : 'false'}
            disabled={loading || disabled}
          >
            <p className='text-overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left'>
              {loading
                ? '...loading'
                : value
                  ? options.find((opt) => opt.value === value)?.label || defaultLabel || 'Select an option'
                  : selectedOption
                    ? selectedOption.label
                    : defaultLabel || 'Select an option'}
            </p>

            {loading ? (
              <svg
                className="animate-spin h-5 w-5 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            ) : (
              <svg
                className={`h-4 w-4 sm:h-5 sm:w-5 text-black flex-shrink-0 ml-2 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 12a1 1 0 01-.707-.293l-4-4a1 1 0 111.414-1.414L10 9.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0110 12z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        </span>
      </div>
      {isOpen && (
        <div className="origin-top-right absolute z-50 mt-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 max-h-[300px] overflow-y-auto">
          <div
            className="py-1"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            {loading ? (
              <p className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-500 flex items-center">
                <svg
                  className="animate-spin h-5 w-5 text-gray-500 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Loading options...
              </p>
            ) : options.length ? (
              options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelectOption(option)}
                  className="block px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 w-full text-left hover:bg-gray-100 focus:outline-none transition-colors"
                  role="menuitem"
                >
                  {option.label}
                </button>
              ))
            ) : (
              <button className="block px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700" role="menuitem">
                {emptyMessage || 'No options available'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dropdown
